"use client";

import { useState, useMemo, useCallback, useTransition } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Paper,
  Tabs,
  Badge,
  Text,
  TbActivity,
  TbLeaf,
  TbCalendar,
  TbExternalLink,
  TbFileText,
  TbChartLine,
  Collapsible,
  DataTable,
  type Column,
  Box,
  Button,
} from "@forestsync/ui";
import { TokenList } from "@/components/glassroom/common";
import { loadSinkTokensByYear } from "@/actions/sink-detail";
import type { CarbonEvent, TokenListItem } from "@/types/glassroom";
import type { loadSinkDocuments, MarketData } from "@/actions/sink-detail";
import { MarketDataTab } from "./MarketDataTab";

type SinkDocument = Awaited<ReturnType<typeof loadSinkDocuments>>[number];

interface EventsTokensClientProps {
  sinkRefId: string;
  events: CarbonEvent[];
  tokenCountsByYear: Record<number, { count: number; creditClass: number | null }>;
  documents: SinkDocument[];
  marketData: MarketData | null;
  creditType?: string | null;
  vintage?: number | null;
}

interface YearTokensState {
  tokens: TokenListItem[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
}

export function EventsTokensClient({
  sinkRefId,
  events,
  tokenCountsByYear,
  documents,
  marketData,
  creditType,
  vintage,
}: EventsTokensClientProps) {
  const t = useTranslations();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<string>("events");
  const [tokensByYear, setTokensByYear] = useState<
    Record<string, YearTokensState>
  >({});
  const [openYear, setOpenYear] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  // Group events by year
  const eventsByYear = useMemo(() => {
    return events.reduce(
      (acc, event) => {
        const year = event.year.toString();
        if (!acc[year]) acc[year] = [];
        acc[year].push(event);
        return acc;
      },
      {} as Record<string, CarbonEvent[]>,
    );
  }, [events]);

  // Calculate total tokens across all vintages
  const totalTokens = useMemo(() => {
    return Object.values(tokenCountsByYear).reduce(
      (sum, entry) => sum + entry.count,
      0,
    );
  }, [tokenCountsByYear]);

  // Vintage years that actually have tokens, newest first (drives the Credits tab).
  const vintageYears = useMemo(
    () =>
      Object.keys(tokenCountsByYear).sort((a, b) => parseInt(b) - parseInt(a)),
    [tokenCountsByYear],
  );

  // Label a vintage section: "Vintage 2023 · Ex-post", or "Unclassified" for legacy (key 0).
  const vintageLabel = useCallback(
    (yearKey: string): string => {
      if (yearKey === "0") return t("sink.unclassified");
      const cls = tokenCountsByYear[parseInt(yearKey)]?.creditClass;
      const base = t("sink.vintageLabel", { year: yearKey });
      if (cls === 1) return `${base} · ${t("sink.exAnte")}`;
      if (cls === 2) return `${base} · ${t("sink.exPost")}`;
      return base;
    },
    [tokenCountsByYear, t],
  );

  // Load tokens for a specific year and page
  const loadTokensForYear = useCallback(
    async (year: string, page: number = 1): Promise<void> => {
      const yearNum = parseInt(year);
      const currentState = tokensByYear[year];

      if (currentState?.isLoading) return;

      setTokensByYear((prev) => ({
        ...prev,
        [year]: {
          tokens: currentState?.tokens || [],
          totalCount: currentState?.totalCount || 0,
          totalPages: currentState?.totalPages || 0,
          currentPage: page,
          isLoading: true,
        },
      }));

      return new Promise((resolve, reject) => {
        startTransition(async () => {
          try {
            const result = await loadSinkTokensByYear(
              sinkRefId,
              yearNum,
              page,
              50,
            );

            setTokensByYear((prev) => ({
              ...prev,
              [year]: {
                tokens: result.tokens,
                totalCount: result.totalCount,
                totalPages: result.totalPages,
                currentPage: result.page,
                isLoading: false,
              },
            }));
            resolve();
          } catch (error) {
            console.error(t("sink.failedToLoadTokens"), year, error);
            setTokensByYear((prev) => ({
              ...prev,
              [year]: {
                tokens: currentState?.tokens || [],
                totalCount: 0,
                totalPages: 0,
                currentPage: page,
                isLoading: false,
              },
            }));
            reject(error);
          }
        });
      });
    },
    [sinkRefId, tokensByYear, t],
  );

  const handleYearToggle = useCallback(
    (year: string, isOpen: boolean) => {
      if (isOpen) {
        setOpenYear(year);
        if (!tokensByYear[year] && activeTab === "tokens") {
          loadTokensForYear(year, 1);
        }
      } else {
        setOpenYear(null);
      }
    },
    [loadTokensForYear, tokensByYear, activeTab],
  );

  const handleTokenPageChange = useCallback(
    async (year: string, page: number): Promise<void> => {
      return loadTokensForYear(year, page);
    },
    [loadTokensForYear],
  );

  // FIX: I updated it but it needs to be fixed I think date of document
  // is a string not a date object...
  const formatDate = (value: Date | string | null | undefined) => {
    if (!value) return "–";

    const date = value instanceof Date ? value : new Date(value);

    if (isNaN(date.getTime())) return "–";

    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Event table columns
  const eventColumns: Column<{
    id: string;
    refId: string;
    eventTypeName: string;
    description: string;
    tokenCount: number;
    timestamp: Date;
    transactionRefId?: string;
  }>[] = [
    {
      key: "refId",
      label: t("common.refId"),
      render: (value) => (
        <Text size="sm" className="font-mono text-gray-500">
          {value}
        </Text>
      ),
    },
    {
      key: "eventTypeName",
      label: t("sink.eventType"),
      render: (value) => (
        <Badge variant="light" color="blue" size="sm">
          {value}
        </Badge>
      ),
    },
    {
      key: "description",
      label: t("common.description"),
      render: (value) => <Text size="sm">{value}</Text>,
    },
    {
      key: "tokenCount",
      label: t("sink.tokensAffected"),
      render: (value) => (
        <Text size="sm" className="text-gray-600 dark:text-gray-400">
          {value > 0 ? value : "-"}
        </Text>
      ),
    },
    {
      key: "timestamp",
      label: t("sink.timestamp"),
      render: (value) => (
        <Text size="sm" className="text-gray-600 dark:text-gray-400">
          {formatDate(value)}
        </Text>
      ),
    },
    {
      key: "transactionRefId",
      label: t("sink.transaction"),
      render: (value, item) => {
        if (!item.transactionRefId) {
          return (
            <Text size="sm" className="text-gray-400">
              -
            </Text>
          );
        }
        return (
          <Button
            component={Link}
            href={`/transaction/${item.transactionRefId}`}
            variant="light"
            size="xs"
            leftSection={<TbExternalLink size={12} />}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {t("sink.viewTransaction")}
          </Button>
        );
      },
    },
  ];

  const documentColumns: Column<SinkDocument>[] = [
    {
      key: "fileName",
      label: t("sink.document.fileName"),
      render: (value) => (
        <Text size="sm" fw={500}>
          {value}
        </Text>
      ),
    },
    {
      key: "type",
      label: t("sink.document.type"),
      render: (value) => (
        <Badge variant="light" color="violet">
          {value}
        </Badge>
      ),
    },
    {
      key: "uploadedAt",
      label: t("sink.document.uploadDate"),
      render: (value) => <Text size="sm">{formatDate(value)}</Text>,
    },
    {
      key: "uploadedBy",
      label: t("sink.document.uploadedBy"),
      render: (value: string) => (
        <Text size="sm" c="dimmed">
          {value?.endsWith("@forestsync.com") ? t("sink.document.forestSyncAdmin") : value}
        </Text>
      ),
    },
    {
      key: "fileSize",
      label: t("sink.document.fileSize"),
      render: (value) => (
        <Text size="sm">{(value / 1024 / 1024).toFixed(2)} MB</Text>
      ),
    },
    {
      key: "url",
      label: "",
      render: (_, item) => (
        <Button
          size="xs"
          variant="light"
          leftSection={<TbExternalLink size={12} />}
          onClick={() => {
            if (item.url) window.open(item.url, "_blank");
          }}
        >
          {t("common.open")}
        </Button>
      ),
    },
  ];

  return (
    <Paper withBorder p={{ base: "xs", sm: "md" }} radius="md">
      <Tabs
        value={activeTab}
        onChange={(value) => {
          if (value) {
            setActiveTab(value);
            setOpenYear(null); // Reset open year when switching tabs
          }
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="events" leftSection={<TbActivity size={16} />}>
            {t("sink.events")} ({events.length})
          </Tabs.Tab>
          <Tabs.Tab value="tokens" leftSection={<TbLeaf size={16} />}>
            {t("sink.co2Registry")} ({totalTokens})
          </Tabs.Tab>
          <Tabs.Tab value="documents" leftSection={<TbFileText size={16} />}>
            {t("sink.documents")} ({documents.length})
          </Tabs.Tab>
          <Tabs.Tab value="market" leftSection={<TbChartLine size={16} />}>
            {t("glassroom.marketData.title")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="events" pt="md">
          <div className="space-y-2">
            {Object.entries(eventsByYear)
              .sort(([a], [b]) => parseInt(b) - parseInt(a))
              .map(([year, yearEvents]) => (
                <Collapsible
                  key={year}
                  label={year}
                  count={yearEvents.length}
                  icon={<TbCalendar size={18} className="text-blue-500" />}
                  depth={0}
                  color="blue"
                  expanded={openYear === year}
                  onToggle={(isOpen) => handleYearToggle(year, isOpen)}
                >
                  <Box pl={{ base: "xs", sm: "md" }}>
                    <DataTable
                      data={yearEvents.map((event) => ({
                        id: event.refId, // Use refId as the unique identifier
                        refId: event.refId,
                        eventTypeName: event.eventTypeName,
                        description: event.description,
                        tokenCount: event.tokenCount,
                        timestamp: event.timestamp,
                        transactionRefId: event.transactionRefId,
                      }))}
                      columns={eventColumns}
                      itemsPerPage={20}
                      compact={true}
                      emptyMessage={t("sink.noEventsFoundForYear")}
                      onRowClick={(item) => {
                        router.push(`/event/${item.refId}`);
                      }}
                    />
                  </Box>
                </Collapsible>
              ))}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="tokens" pt="md">
          <div className="space-y-2">
            {vintageYears.map((year) => {
              const yearState = tokensByYear[year];
              const count = tokenCountsByYear[parseInt(year)]?.count || 0;

              return (
                <Collapsible
                  key={year}
                  label={vintageLabel(year)}
                  count={count}
                  icon={<TbLeaf size={18} className="text-green-500" />}
                  depth={0}
                  color="green"
                  expanded={openYear === year}
                  onToggle={(isOpen) => handleYearToggle(year, isOpen)}
                >
                  <Box pl={{ base: "xs", sm: "md" }}>
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out">
                      {yearState ? (
                        yearState.isLoading && yearState.tokens.length === 0 ? (
                          <TokenList
                            tokens={[]}
                            isLoading={true}
                            maxHeight={400}
                            emptyMessage={t("sink.loadingTokensForYear", {
                              year,
                            })}
                            currentPage={1}
                            totalPages={1}
                            onPageChange={async () => {}}
                          />
                        ) : yearState.tokens.length > 0 ? (
                          <TokenList
                            tokens={yearState.tokens}
                            isLoading={false}
                            maxHeight={400}
                            emptyMessage={t("sink.noTokensFoundForYear", {
                              year,
                            })}
                            currentPage={yearState.currentPage}
                            totalPages={yearState.totalPages}
                            onPageChange={async (page: number) =>
                              handleTokenPageChange(year, page)
                            }
                          />
                        ) : (
                          <Text className="text-gray-500 dark:text-gray-400 text-center py-8">
                            {t("sink.noTokensFoundForYear", { year })}
                          </Text>
                        )
                      ) : (
                        <Text className="text-gray-500 dark:text-gray-400 text-center py-8">
                          {t("sink.clickToLoadTokens")}
                        </Text>
                      )}
                    </div>
                  </Box>
                </Collapsible>
              );
            })}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="md">
          <DataTable
            data={documents.map((doc) => ({ ...doc, id: doc.id }))}
            columns={documentColumns}
            itemsPerPage={20}
            emptyMessage={t("sink.noDocumentsFound")}
          />
        </Tabs.Panel>

        <Tabs.Panel value="market" pt="md">
          <MarketDataTab data={marketData} creditType={creditType} vintage={vintage} />
        </Tabs.Panel>
      </Tabs>
    </Paper>
  );
}
